import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FaEdit, FaTrash } from 'react-icons/fa';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import RTModal from '../common/RTModal';
import Table from '../common/Table';
import InvoicePrint from './InvoicePrint';
import AddInvoiceItem from './AddInvoiceItem';
import AddPayment from './AddPayment';
import AddAdjustment from './AddAdjustment';
import { apiGet, apiPost } from '../../api/apiClient';
import {
  toastSuccess,
  toastError,
  toastConfirm,
  toastWarning,
} from '../common/toast';
import { useSessionInfo } from '../../hooks/useSessionInfo';
import { hasPermission } from '../../utils/permissions';
import '../common/rtCommon.css';
import './ViewInvoiceDetails.css';
import RTPageLayout from '../common/RTPageLayout';
import RTStatusPill, { formatStatusLabel } from '../common/statusPill';

const ViewInvoiceDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { invoiceId } = useParams();
  const {
    displayName,
    clientName,
    addressLine1,
    addressLine2,
    city,
    state,
    postalCode,
  } = useSessionInfo();
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [showPrintComponent, setShowPrintComponent] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAddAdjustment, setShowAddAdjustment] = useState(false);
  const [pgDetails, setPgDetails] = useState(null);
  const [paymentProvider, setPaymentProvider] = useState(null);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editItemForm, setEditItemForm] = useState({
    amount: '',
    description: '',
  });

  useEffect(() => {
    if (invoiceId) {
      loadInvoiceDetails();
    }
    loadPaymentProvider();
  }, [invoiceId]);

  const loadPaymentProvider = async () => {
    try {
      const response = await apiGet('/payments/settings');
      const data = response.data;
      if (data.payment_provider?.value) {
        try {
          const providerData = JSON.parse(data.payment_provider.value);
          setPaymentProvider(providerData.selected || 'none');
        } catch (e) {
          setPaymentProvider(data.payment_provider.value);
        }
      } else {
        setPaymentProvider('none');
      }
    } catch (error) {
      console.error('Error loading payment provider:', error);
      setPaymentProvider('none');
    }
  };

  const loadInvoiceDetails = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`/invoicing/invoices/${invoiceId}`);
      if (response && response.data) {
        setInvoice(response.data);
      }
    } catch (error) {
      console.error('Error loading invoice details:', error);
      toastError('Failed to load invoice details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvoice = async () => {
    setShowSendModal(true);
  };

  const confirmSendInvoice = async () => {
    try {
      setProcessing(true);
      setProcessingMessage('Sending invoice...');
      await apiPost(`/invoicing/invoices/${invoiceId}/update`, {
        status: 'sent',
      });
      toastSuccess('Invoice sent successfully.');
      setShowSendModal(false);
      await loadInvoiceDetails(); // Reload to get updated status
    } catch (error) {
      console.error('Error sending invoice:', error);
      toastError('Failed to send invoice. Please try again.');
    } finally {
      setProcessing(false);
      setProcessingMessage('');
    }
  };

  const handleCancelInvoice = async () => {
    setShowCancelModal(true);
  };

  const confirmCancelInvoice = async () => {
    try {
      setProcessing(true);
      setProcessingMessage('Cancelling invoice...');
      await apiPost(`/invoicing/invoices/${invoiceId}/update`, {
        status: 'cancelled',
      });
      toastSuccess('Invoice cancelled successfully.');
      setShowCancelModal(false);
      await loadInvoiceDetails(); // Reload to get updated status
    } catch (error) {
      console.error('Error cancelling invoice:', error);
      toastError('Failed to cancel invoice. Please try again.');
    } finally {
      setProcessing(false);
      setProcessingMessage('');
    }
  };

  const handleAddInvoiceItem = () => {
    setShowAddItemModal(true);
  };

  const handleCloseAddItemModal = () => {
    setShowAddItemModal(false);
  };

  const handleItemAdded = () => {
    // Refresh invoice details after item is added
    loadInvoiceDetails();
  };

  const handleEditInvoiceItem = (item) => {
    setEditingItem(item);
    setEditItemForm({
      amount: item.amount !== undefined ? item.amount : '',
      description: item.description || '',
    });
    setShowEditItemModal(true);
  };

  const handleEditItemFormChange = (e) => {
    const { name, value } = e.target;
    setEditItemForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEditItem = async () => {
    if (!editingItem) return;
    const amount = parseFloat(editItemForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toastError('Please enter a valid amount greater than 0');
      return;
    }
    try {
      setProcessing(true);
      setProcessingMessage('Updating invoice item...');
      await apiPost(
        `/invoicing/invoice-items/${editingItem.invoice_item_id || editingItem.item_id}/update`,
        {
          amount,
          description: editItemForm.description || null,
        },
      );
      toastSuccess('Invoice item updated successfully');
      setShowEditItemModal(false);
      setEditingItem(null);
      loadInvoiceDetails();
    } catch (error) {
      console.error('Error updating invoice item:', error);
      toastError(
        error.response?.data?.detail ||
          'Failed to update invoice item. Please try again.',
      );
    } finally {
      setProcessing(false);
      setProcessingMessage('');
    }
  };

  const handleDeleteInvoiceItem = async (itemId) => {
    if (!invoice || invoice.status !== 'draft') {
      toastError('Can only delete items from draft invoices');
      return;
    }

    try {
      const confirmed = await toastConfirm(
        true,
        'Are you sure you want to delete this invoice item? This action cannot be undone.',
      );

      if (!confirmed) return;

      setProcessing(true);
      setProcessingMessage('Deleting invoice item...');
      const response = await apiPost(
        `/invoicing/invoice-items/${itemId}/delete`,
        {},
      );

      if (response) {
        toastSuccess('Invoice item deleted successfully');
        loadInvoiceDetails(); // Refresh the invoice data
      } else {
        toastError('Failed to delete invoice item');
      }
    } catch (error) {
      console.error('Error deleting invoice item:', error);
      if (error.response?.data?.detail) {
        toastError(error.response.data.detail);
      } else {
        toastError('Failed to delete invoice item. Please try again.');
      }
    } finally {
      setProcessing(false);
      setProcessingMessage('');
    }
  };

  const handleAddPayment = async () => {
    try {
      // Fetch PG details if not already loaded
      if (invoice && !pgDetails) {
        const pgResponse = await apiGet(`/pgdetails/${invoice.pg_id}`);
        if (pgResponse?.data) {
          setPgDetails(pgResponse.data);
          setShowAddPayment(true);
        } else {
          toastError('Failed to load paying guest details');
        }
      } else if (pgDetails) {
        setShowAddPayment(true);
      }
    } catch (error) {
      console.error('Error loading PG data:', error);
      toastError('Failed to load paying guest data');
    }
  };

  const handleCloseAddPayment = () => {
    setShowAddPayment(false);
    // Reload invoice details to reflect any payments made
    loadInvoiceDetails();
  };

  const handleAddAdjustment = async () => {
    try {
      if (invoice && !pgDetails) {
        const pgResponse = await apiGet(`/pgdetails/${invoice.pg_id}`);
        if (pgResponse?.data) {
          setPgDetails(pgResponse.data);
          setShowAddAdjustment(true);
        } else {
          toastError('Failed to load paying guest details');
        }
      } else if (pgDetails) {
        setShowAddAdjustment(true);
      }
    } catch (error) {
      console.error('Error loading PG data:', error);
      toastError('Failed to load paying guest data');
    }
  };

  const handleCloseAddAdjustment = (dataChanged) => {
    setShowAddAdjustment(false);
    if (dataChanged) {
      loadInvoiceDetails();
    }
  };

  const handleGeneratePaymentLink = async () => {
    try {
      // Show confirmation toast
      const confirmed = await toastConfirm(
        true,
        'Are you sure you want to generate a payment link for this invoice? A WhatsApp notification will be sent to the customer.',
      );

      if (!confirmed) {
        return;
      }

      setProcessing(true);
      setProcessingMessage('Generating payment link...');
      const response = await apiPost(
        `/invoicing/invoices/${invoiceId}/generate-payment-link`,
        {},
      );

      if (response?.data?.success) {
        // Check if WhatsApp was sent successfully
        if (response.data.whatsapp_sent) {
          toastSuccess('Payment link generated and sent via WhatsApp');
        } else if (response.data.whatsapp_error) {
          toastWarning(
            `Payment link generated but WhatsApp failed: ${response.data.whatsapp_error}`,
          );
        } else {
          toastSuccess('Payment link generated successfully');
        }
        loadInvoiceDetails(); // Reload to get updated payment link
      } else {
        toastError(response?.data?.error || 'Failed to generate payment link');
      }
    } catch (error) {
      console.error('Error generating payment link:', error);
      const errorMsg =
        error?.response?.data?.detail ||
        'Failed to generate payment link. Please try again.';
      toastError(errorMsg);
    } finally {
      setProcessing(false);
      setProcessingMessage('');
    }
  };

  const handleCancelPaymentLink = async () => {
    try {
      const confirmed = await toastConfirm(
        true,
        'Are you sure you want to cancel this payment link? The link will become invalid and cannot be used for payments.',
      );

      if (!confirmed) return;

      setProcessing(true);
      setProcessingMessage('Cancelling payment link...');
      const response = await apiPost(
        `/invoicing/invoices/${invoiceId}/cancel-payment-link`,
        {},
      );

      if (response?.data?.success) {
        toastSuccess('Payment link cancelled successfully');
        loadInvoiceDetails(); // Reload to get updated status
      } else {
        toastError(response?.data?.error || 'Failed to cancel payment link');
      }
    } catch (error) {
      console.error('Error cancelling payment link:', error);
      const errorMsg =
        error?.response?.data?.detail ||
        'Failed to cancel payment link. Please try again.';
      toastError(errorMsg);
    } finally {
      setProcessing(false);
      setProcessingMessage('');
    }
  };

  const handleCheckPaymentStatus = async () => {
    try {
      setProcessing(true);
      setProcessingMessage('Checking payment status...');
      const response = await apiGet(
        `/invoicing/invoices/${invoiceId}/payment-link-status`,
      );

      if (response?.data) {
        toastSuccess('Payment status updated');
        loadInvoiceDetails(); // Reload to get updated status
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      toastError('Failed to check payment status. Please try again.');
    } finally {
      setProcessing(false);
      setProcessingMessage('');
    }
  };

  // If AddAdjustment is shown, render only the AddAdjustment component
  if (showAddAdjustment && invoice && pgDetails) {
    return (
      <AddAdjustment
        onClose={handleCloseAddAdjustment}
        invoiceData={invoice}
        pgData={pgDetails}
      />
    );
  }

  // If AddPayment is shown, render only the AddPayment component
  if (showAddPayment && invoice && pgDetails) {
    return (
      <AddPayment
        onClose={handleCloseAddPayment}
        preSelectedPG={pgDetails}
        preSelectedInvoiceId={invoice.invoice_id}
        singleInvoiceMode={true}
      />
    );
  }

  const handlePrint = () => {
    setShowPrintComponent(true);
  };

  const handlePrintComplete = () => {
    setShowPrintComponent(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getInvoiceItemsTableData = () => {
    if (!invoice.invoice_items || invoice.invoice_items.length === 0) {
      return [];
    }

    return invoice.invoice_items.map((item, index) => ({
      id: index,
      item_id: item.invoice_item_id || item.item_id || item.id,
      raw_item: item,
      description: (
        <div>
          {item.invoice_item_type}
          {item.description && item.description !== item.invoice_item_type && (
            <>
              <span style={{ color: '#999', margin: '0 4px' }}>-</span>
              {item.description}
            </>
          )}
          {item.discount_amount > 0 && (
            <div
              style={{
                color: '#153C7F',
                fontSize: '0.8rem',
                fontStyle: 'italic',
                marginTop: '2px',
              }}
            >
              Discount: {formatCurrency(item.discount_amount)}
              {item.discount_description && ` (${item.discount_description})`}
            </div>
          )}
        </div>
      ),
      quantity: item.quantity,
      amount: formatCurrency(item.amount),
      tax: formatCurrency(item.tax_amount),
      total: formatCurrency(item.total_amount),
    }));
  };

  const getInvoiceItemsColumns = () => {
    const baseColumns = [
      {
        Header: 'Description',
        accessor: 'description',
        sortable: false,
        Cell: ({ value }) => value,
      },
      {
        Header: 'Qty',
        accessor: 'quantity',
        sortable: false,
      },
      {
        Header: 'Amount',
        accessor: 'amount',
        sortable: false,
      },
      {
        Header: 'Tax',
        accessor: 'tax',
        sortable: false,
      },
      {
        Header: 'Total',
        accessor: 'total',
        sortable: false,
      },
    ];

    // Only add Actions column if draft and user can create/edit invoices
    if (invoice?.status === 'draft' && hasPermission('payments.create_invoice')) {
      baseColumns.push({
        Header: 'Actions',
        accessor: 'actions',
        sortable: false,
        Cell: ({ row }) => {
          const itemId = row.original?.item_id || row.item_id;
          const rawItem = row.original?.raw_item || row.raw_item;

          return (
            <div className="action-buttons">
              <RTButton
                type="icon"
                variant="secondary"
                size="sm"
                permissionKey="payments.create_invoice"
                icon={<FaEdit />}
                onClick={() => {
                  if (!rawItem) {
                    toastError('Cannot edit item: data not found');
                    return;
                  }
                  handleEditInvoiceItem({ ...rawItem, item_id: itemId });
                }}
                disabled={processing}
                title="Edit Item"
              />
              <RTButton
                type="icon"
                variant="danger"
                size="sm"
                permissionKey="payments.create_invoice"
                icon={<FaTrash />}
                onClick={() => {
                  if (!itemId) {
                    toastError('Cannot delete item: ID not found');
                    return;
                  }
                  handleDeleteInvoiceItem(itemId);
                }}
                disabled={processing || !itemId}
                title={processing ? 'Deleting...' : 'Delete Item'}
              />
            </div>
          );
        },
      });
    }

    return baseColumns;
  };

  const formatRoomDetails = (room, roomName) => {
    if (roomName) return roomName;
    if (room?.room_name) return room.room_name;
    if (!room) return 'N/A';
    return room.room_type || 'N/A';
  };

  const getStatusBadge = (status) => (
    <RTStatusPill status={status}>{formatStatusLabel(status)}</RTStatusPill>
  );

  const renderActionButtons = () => {
    if (!invoice) return null;

    const status = invoice.status;
    const buttons = [];

    // Draft and sent status - allow cancellation
    if (status === 'draft' || status === 'sent') {
      buttons.push(
        <RTButton
          key="cancel"
          variant="secondary"
          permissionKey="payments.create_invoice"
          onClick={handleCancelInvoice}
        >
          Cancel Invoice
        </RTButton>,
      );
    }

    // Draft status - allow sending
    if (status === 'draft') {
      buttons.push(
        <RTButton key="send" variant="secondary" permissionKey="payments.create_invoice" onClick={handleSendInvoice}>
          Send Invoice
        </RTButton>,
      );
    }

    // Sent and overdue status buttons
    if (
      status === 'sent' ||
      status === 'overdue' ||
      status === 'partially_paid'
    ) {
      buttons.push(
        <RTButton
          key="add-payment"
          variant="secondary"
          permissionKey="payments.add_payment"
          onClick={handleAddPayment}
        >
          Add Payment
        </RTButton>,
      );
    }

    // Add Adjustment button for sent and partially_paid
    if (status === 'sent' || status === 'partially_paid') {
      buttons.push(
        <RTButton
          key="add-adjustment"
          variant="secondary"
          permissionKey="payments.add_payment"
          onClick={handleAddAdjustment}
        >
          Add Adjustment
        </RTButton>,
      );
    }

    if (
      status === 'sent' ||
      status === 'overdue' ||
      status === 'partially_paid'
    ) {
      // Payment link buttons based on link status
      const linkStatus = invoice.payment_provider_link_status;

      // Show cancel and check status buttons for Active links
      // Status is now standardized to 'Active' for all newly generated links
      // Legacy values ('Generated', 'Created') are supported for backward compatibility
      if (
        linkStatus === 'Active' ||
        linkStatus === 'Generated' ||
        linkStatus === 'Created'
      ) {
        buttons.push(
          <RTButton
            key="check-status"
            variant="secondary"
            onClick={handleCheckPaymentStatus}
          >
            Check Status
          </RTButton>,
        );
      } else if (linkStatus === 'Completed' || linkStatus === 'Paid') {
        // Payment completed - show check status only
        buttons.push(
          <RTButton
            key="check-status"
            variant="secondary"
            onClick={handleCheckPaymentStatus}
          >
            Check Status
          </RTButton>,
        );
      }
    }

    return buttons;
  };

  const renderPrintButton = () => {
    if (!invoice || invoice.status === 'cancelled') return null;

    return (
      <RTButton key="print" variant="secondary" onClick={handlePrint}>
        Print
      </RTButton>
    );
  };

  const renderAddInvoiceItemButton = () => {
    if (!invoice || invoice.status !== 'draft') return null;

    return (
      <RTButton
        requiresWrite
        key="add-item"
        variant="secondary"
        permissionKey="payments.create_invoice"
        onClick={handleAddInvoiceItem}
      >
        Add Invoice Item
      </RTButton>
    );
  };

  if (loading) {
    return (
      <RTPageLayout
        activePath={location.pathname}
        contentClassName="view-invoice-details"
      >
        <RTSpinner message="Loading invoice details..." />
      </RTPageLayout>
    );
  }

  if (processing) {
    return (
      <RTPageLayout
        activePath={location.pathname}
        contentClassName="view-invoice-details"
      >
        <RTSpinner message={processingMessage || 'Processing...'} />
      </RTPageLayout>
    );
  }

  if (!invoice) {
    return (
      <RTPageLayout
        activePath={location.pathname}
        contentClassName="view-invoice-details"
      >
        <div className="error-state">
          <h3>Invoice Not Found</h3>
          <p>The requested invoice could not be found.</p>
          <RTButton variant="primary" onClick={() => navigate(-1)}>
            Go Back
          </RTButton>
        </div>
      </RTPageLayout>
    );
  }

  return (
    <>
      <RTPageLayout
        activePath={location.pathname}
        title="Invoice Details"
        actions={
          <>
            <RTButton variant="secondary" onClick={() => navigate(-1)}>
              ← Back
            </RTButton>
            {renderPrintButton()}
            {renderAddInvoiceItemButton()}
            {renderActionButtons()}
          </>
        }
        mainSection={true}
        contentClassName="view-invoice-details"
      >
        <div className="invoice-container">
          {/* Invoice Header */}
          <div className="invoice-header">
            <div className="invoice-header-left">
              <h1 className="invoice-title">INVOICE</h1>
              <div className="invoice-number">#{invoice.invoice_number}</div>
              {getStatusBadge(invoice.status)}
            </div>
            <div className="invoice-header-right">
              <div className="invoice-dates">
                <p>
                  <strong>Invoice Date:</strong>{' '}
                  {formatDate(invoice.invoice_date)}
                </p>
                <p>
                  <strong>Amount Paid:</strong>{' '}
                  {formatCurrency(invoice.amount_paid || 0)}
                </p>
                <p>
                  <strong>Amount Remaining:</strong>{' '}
                  {formatCurrency(invoice.amount_remaining || 0)}
                </p>
              </div>
            </div>
          </div>

          {/* Billing Information */}
          <div className="billing-section">
            <div className="billing-from">
              <h4>From:</h4>
              <div className="billing-details">
                <strong>{clientName || displayName}</strong>
                {addressLine1 && <p>{addressLine1}</p>}
                {addressLine2 && <p>{addressLine2}</p>}
                {(city || state || postalCode) && (
                  <p>
                    {[city, state].filter(Boolean).join(', ')}
                    {postalCode ? ` - ${postalCode}` : ''}
                  </p>
                )}
              </div>
            </div>
            <div className="billing-to">
              <h4>Bill To:</h4>
              <div className="billing-details">
                <strong>{invoice.paying_guest?.name || 'N/A'}</strong>
                {invoice.paying_guest?.address &&
                  invoice.paying_guest.address !== 'N/A' && (
                    <p>{invoice.paying_guest.address}</p>
                  )}
                {invoice.paying_guest?.mobile && (
                  <p>Phone: {invoice.paying_guest.mobile}</p>
                )}
                <p>{invoice.building?.name || 'N/A'}</p>
                <p>{formatRoomDetails(invoice.room, invoice.room_name)}</p>
              </div>
            </div>
          </div>

          {/* Billing Period */}
          <div className="billing-period">
            <h4>Billing Period:</h4>
            <p>
              {formatDate(invoice.billing_period_start)} to{' '}
              {formatDate(invoice.billing_period_end)}
            </p>
          </div>

          {/* Payment Link Section - Hidden in print */}
          {(invoice.status === 'sent' ||
            invoice.status === 'overdue' ||
            invoice.status === 'partially_paid') && (
            <div className="payment-link-section no-print">
              <h4>Payment Link Details</h4>
              {invoice.payment_provider_link ? (
                <div className="payment-link-details">
                  <div className="payment-link-row">
                    <span className="payment-link-label">Provider:</span>
                    <span className="payment-link-value">
                      {invoice.payment_provider_name || 'N/A'}
                    </span>
                  </div>
                  <div className="payment-link-row">
                    <span className="payment-link-label">Transaction ID:</span>
                    <span className="payment-link-value">
                      {invoice.payment_provider_transaction_id || 'N/A'}
                    </span>
                  </div>
                  <div className="payment-link-row">
                    <span className="payment-link-label">Order ID:</span>
                    <span className="payment-link-value">
                      {invoice.payment_provider_order_id || 'N/A'}
                    </span>
                  </div>
                  <div className="payment-link-row">
                    <span className="payment-link-label">Status:</span>
                    <span
                      className={`payment-link-status status-${(invoice.payment_provider_link_status || 'unknown').toLowerCase().replace(' ', '-')}`}
                    >
                      {invoice.payment_provider_link_status || 'Unknown'}
                    </span>
                  </div>
                  <div className="payment-link-row">
                    <span className="payment-link-label">Payment Link:</span>
                    <a
                      href={invoice.payment_provider_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="payment-link-url"
                    >
                      {invoice.payment_provider_link}
                    </a>
                  </div>
                  <div className="payment-link-actions">
                    {!['Cancelled', 'Expired', 'Failed'].includes(
                      invoice.payment_provider_link_status,
                    ) && (
                      <>
                        <RTButton
                          variant="secondary"
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              invoice.payment_provider_link,
                            );
                            toastSuccess('Payment link copied to clipboard');
                          }}
                        >
                          Copy Link
                        </RTButton>
                        <RTButton
                          variant="secondary"
                          size="small"
                          onClick={() =>
                            window.open(invoice.payment_provider_link, '_blank')
                          }
                        >
                          Open Link
                        </RTButton>
                      </>
                    )}
                    {['Active', 'Generated', 'Created'].includes(
                      invoice.payment_provider_link_status,
                    ) && (
                      <RTButton
                        variant="danger"
                        size="small"
                        permissionKey="payments.create_invoice"
                        onClick={handleCancelPaymentLink}
                      >
                        Cancel Payment Link
                      </RTButton>
                    )}
                    {['Cancelled', 'Expired', 'Failed'].includes(
                      invoice.payment_provider_link_status,
                    ) &&
                      paymentProvider !== 'none' && (
                        <RTButton
                          variant="secondary"
                          size="small"
                          permissionKey="payments.create_invoice"
                          onClick={handleGeneratePaymentLink}
                        >
                          Generate Payment Link
                        </RTButton>
                      )}
                  </div>
                </div>
              ) : (
                <div className="payment-link-empty">
                  {paymentProvider !== 'none' ? (
                    <>
                      <p>
                        No payment link has been generated for this invoice yet.
                      </p>
                      <RTButton
                        variant="secondary"
                        size="small"
                        permissionKey="payments.create_invoice"
                        onClick={handleGeneratePaymentLink}
                      >
                        Generate Payment Link
                      </RTButton>
                    </>
                  ) : (
                    <p className="text-muted">
                      No payment provider configured. Go to Settings &gt;
                      Payment Settings to enable.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Invoice Items */}
          <div className="invoice-items-section">
            <h4>Invoice Items</h4>

            {/* Warning message for empty invoice items */}
            {(!invoice.invoice_items || invoice.invoice_items.length === 0) &&
              invoice.status === 'draft' && (
                <div className="empty-items-warning">
                  <div className="warning-message">
                    <span className="warning-icon">⚠️</span>
                    <div className="warning-text">
                      <strong>No invoice items found!</strong>
                      <p>
                        Please add at least one item to this invoice or cancel
                        the invoice if it's not needed.
                      </p>
                    </div>
                  </div>
                </div>
              )}

            <Table
              data={getInvoiceItemsTableData()}
              columns={getInvoiceItemsColumns()}
              showSearch={false}
              exportable={false}
              title=""
              noDataText="No invoice items found"
            />
          </div>

          {/* Invoice Total */}
          <div className="invoice-total-section">
            <div className="total-row">
              <span className="view-invoice-total-label">Total Amount:</span>
              <span className="view-invoice-total-amount">
                {formatCurrency(invoice.total_amount)}
              </span>
            </div>
            <div className="total-row">
              <span className="view-invoice-total-label">Amount Paid:</span>
              <span className="view-invoice-total-amount">
                {formatCurrency(invoice.amount_paid || 0)}
              </span>
            </div>
            <div className="total-row final-total">
              <span className="view-invoice-total-label">
                Amount Remaining:
              </span>
              <span className="view-invoice-total-amount">
                {formatCurrency(invoice.amount_remaining || 0)}
              </span>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="invoice-notes">
              <h4>Notes:</h4>
              <p>{invoice.notes}</p>
            </div>
          )}

          {/* Footer */}
          <div className="invoice-footer">
            <p>Thank you for your business!</p>
            <small>Generated on {formatDate(new Date())}</small>
          </div>
        </div>
      </RTPageLayout>

      {/* Send Confirmation Modal */}
      <RTModal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        title="Send Invoice"
        size="medium"
      >
        <div className="modal-content">
          <p>
            You are about to send invoice{' '}
            <strong>#{invoice?.invoice_number}</strong> to{' '}
            {invoice?.paying_guest?.name}. Once sent, the invoice will be marked
            as "Sent" and cannot be edited.
          </p>
          <p>Are you sure you want to proceed?</p>
          <div className="modal-actions">
            <RTButton
              variant="secondary"
              onClick={() => setShowSendModal(false)}
              disabled={processing}
            >
              Back
            </RTButton>
            <RTButton
              variant="primary"
              onClick={confirmSendInvoice}
              loading={processing}
              disabled={processing}
            >
              {processing ? 'Sending...' : 'Send Invoice'}
            </RTButton>
          </div>
        </div>
      </RTModal>

      {/* Cancel Confirmation Modal */}
      <RTModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        title="Cancel Invoice"
        size="medium"
      >
        <div className="modal-content">
          <p>
            You are about to cancel invoice{' '}
            <strong>#{invoice?.invoice_number}</strong>. This will mark it as
            "Cancelled" and it cannot be recovered.
          </p>
          <p>Are you sure you want to proceed?</p>
          <div className="modal-actions">
            <RTButton
              variant="secondary"
              onClick={() => setShowCancelModal(false)}
              disabled={processing}
            >
              Back
            </RTButton>
            <RTButton
              variant="danger"
              onClick={confirmCancelInvoice}
              loading={processing}
              disabled={processing}
            >
              {processing ? 'Cancelling...' : 'Cancel Invoice'}
            </RTButton>
          </div>
        </div>
      </RTModal>

      {/* Edit Invoice Item Modal */}
      <RTModal
        isOpen={showEditItemModal}
        onClose={() => {
          setShowEditItemModal(false);
          setEditingItem(null);
        }}
        title="Edit Invoice Item"
        size="medium"
      >
        <div className="modal-content">
          <div className="form-group">
            <label htmlFor="edit-amount">Amount (₹)</label>
            <input
              id="edit-amount"
              type="number"
              name="amount"
              className="rt-input"
              value={editItemForm.amount}
              onChange={handleEditItemFormChange}
              min="0.01"
              step="0.01"
            />
          </div>
          <div className="form-group" style={{ marginTop: '12px' }}>
            <label htmlFor="edit-description">Description</label>
            <input
              id="edit-description"
              type="text"
              name="description"
              className="rt-input"
              value={editItemForm.description}
              onChange={handleEditItemFormChange}
              placeholder="Optional description"
            />
          </div>
          <div className="modal-actions" style={{ marginTop: '16px' }}>
            <RTButton
              variant="secondary"
              onClick={() => {
                setShowEditItemModal(false);
                setEditingItem(null);
              }}
              disabled={processing}
            >
              Cancel
            </RTButton>
            <RTButton
              variant="primary"
              onClick={handleSaveEditItem}
              loading={processing}
              disabled={processing}
            >
              {processing ? 'Saving...' : 'Save Changes'}
            </RTButton>
          </div>
        </div>
      </RTModal>

      {/* Invoice Print Component */}
      {showPrintComponent && (
        <InvoicePrint invoiceId={invoiceId} onClose={handlePrintComplete} />
      )}

      {/* Add Invoice Item Modal */}
      {showAddItemModal && invoice && (
        <AddInvoiceItem
          invoice={invoice}
          onClose={handleCloseAddItemModal}
          onItemAdded={handleItemAdded}
        />
      )}
    </>
  );
};

export default ViewInvoiceDetails;
